"use client";

import { Amplify } from "aws-amplify";
import { useEffect, useState } from "react";

interface AmplifyProviderProps {
  children: React.ReactNode;
  userPoolId?: string;
  userPoolClientId?: string;
}

/**
 * Amplify 설정을 초기화하는 Provider
 */
export function AmplifyProvider({ children, userPoolId: propsUserPoolId, userPoolClientId: propsUserPoolClientId }: AmplifyProviderProps) {
  const [isConfigured, setIsConfigured] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    // Props로 전달된 값이 있으면 우선 사용, 없으면 환경 변수 사용
    const userPoolId = propsUserPoolId || process.env.NEXT_PUBLIC_USER_POOL_ID;
    const userPoolClientId = propsUserPoolClientId || process.env.NEXT_PUBLIC_USER_POOL_CLIENT_ID;

    if (process.env.NODE_ENV === 'development') {
      console.log("[AmplifyProvider] Configuring with:", { userPoolId, userPoolClientId });
    }

    if (userPoolId && userPoolClientId) {
      try {
        Amplify.configure(
          {
            Auth: {
              Cognito: {
                userPoolId,
                userPoolClientId,
                signUpVerificationMethod: "code",
                loginWith: {
                  username: true,
                },
              },
            },
          },
          { ssr: true }
        );
        console.log("[AmplifyProvider] Configuration successful");
        setIsConfigured(true);
      } catch (error) {
        console.error("[AmplifyProvider] Configuration error:", error);
        setConfigError(error instanceof Error ? error.message : "Unknown error");
      }
    } else {
      const error = `[AmplifyProvider] Missing environment variables. UserPoolId: ${userPoolId ? "present" : "missing"}, ClientId: ${userPoolClientId ? "present" : "missing"}`;
      console.error(error);
      setConfigError(error);
    }
  }, [propsUserPoolId, propsUserPoolClientId]);

  // Amplify 설정이 완료될 때까지 자식 컴포넌트 렌더링 차단
  if (configError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md">
          <h2 className="text-xl font-bold text-red-600 mb-4">인증 설정 오류</h2>
          <p className="text-gray-700 mb-4">Amplify 설정 중 오류가 발생했습니다.</p>
          <pre className="text-sm text-gray-600 bg-gray-100 p-4 rounded overflow-auto">
            {configError}
          </pre>
        </div>
      </div>
    );
  }

  if (!isConfigured) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">인증 시스템 초기화 중...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}